import React, { memo } from 'react';
import { cn } from '@/shared/utils/cn';
import { Text } from '@/modules/Text';
import { Title } from '@/modules/Title';
import { TextListWithDataProps } from '../../types';
import { TextListIndicator } from '../atoms';
import { getColorValue } from '../../utils/colors';

/**
 * TextListWithData - Texto con indicador de color y valor
 *
 * Muestra un texto con un indicador circular de color y un valor asociado.
 * Útil para mostrar etiquetas con datos numéricos o textuales.
 *
 * @example
 * ```tsx
 * <TextListWithData
 *   title="Completado"
 *   color="success"
 *   data="60%"
 * />
 * ```
 *
 * @example
 * ```tsx
 * <TextListWithData
 *   title="Tareas"
 *   color="brand"
 *   data={42}
 *   indicatorSize={12}
 * />
 * ```
 */
export const TextListWithData = memo(
  ({
    title,
    color,
    data,
    className,
    disabled = false,
    indicatorSize = 8,
    showIndicator = true,
  }: TextListWithDataProps) => {
    // Normalizar valores
    const normalizedTitle = title != null ? String(title) : '';
    const normalizedData = data != null ? String(data) : '';
    const normalizedColor = getColorValue(color);
    const normalizedIndicatorSize = Math.max(4, Math.min(32, Number(indicatorSize) || 8));

    // Clases del contenedor
    const containerClasses = cn(
      'ui-flex ui-flex-wrap ui-items-center ui-justify-between ui-w-full ui-gap-4',
      disabled && 'ui-opacity-50 ui-pointer-events-none',
      className,
    );

    return (
      <div className={cn('ui-imj', containerClasses)} role="listitem">
        {/* Indicador y título */}
        <div className="ui-flex ui-items-center ui-gap-4">
          {showIndicator && (
            <TextListIndicator color={normalizedColor} size={normalizedIndicatorSize} />
          )}
          {normalizedTitle && (
            <Text type="body" size="sm" color="primary">
              {normalizedTitle}
            </Text>
          )}
        </div>

        {/* Valor */}
        {normalizedData && (
          <Title className="ui-text-heading-h6 ui-font-heading-h6 ui-leading-heading-h6 ui-tracking-heading-h6 ui-text-primary">
            {normalizedData}
          </Title>
        )}
      </div>
    );
  },
);

TextListWithData.displayName = 'TextListWithData';
